import { Server, FolderTree, Globe2, Boxes, CloudCog, IdCard, Network, Database, ShieldCheck } from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const SERVICES = [
  {
    id: "windows-linux",
    icon: Server,
    title: "Windows Server e Linux",
    desc: "Instalação, hardening, parametrização e administração avançada de servidores corporativos, com padrões de segurança e documentação completa do ambiente.",
    items: [
      "Instalação e configuração de Windows Server e distribuições Linux corporativas",
      "Hardening de sistema, atualizações gerenciadas e linha de base de segurança",
      "Administração de funções, serviços e agendamentos críticos",
      "Documentação técnica e procedimentos operacionais do parque de servidores",
    ],
  },
  {
    id: "directory-dns",
    icon: FolderTree,
    title: "Active Directory, DNS, DHCP e GPO",
    desc: "Estruturação de domínio, políticas de grupo granulares, resolução de nomes resiliente e governança centralizada de identidades e acessos.",
    items: [
      "Planejamento e manutenção de domínio Active Directory com controle granular",
      "Organização de Unidades Organizacionais e políticas de grupo (GPO) por perfil de usuário",
      "DNS corporativo com zonas internas, encaminhadores e replicação confiável",
      "DHDHCP com escopos, reservas e opções conforme o desenho de rede",
    ],
  },
  {
    id: "hyperv-virtualization",
    icon: Boxes,
    title: "Virtualização Hyper-V e alta disponibilidade",
    desc: "Consolidação de hardware, clusters de failover, otimização de recursos e réplicas de máquinas virtuais para máxima disponibilidade dos sistemas.",
    items: [
      "Projetos de virtualização com Hyper-V e failover clustering",
      "Consolidação de servidores físicos e otimização de recursos computacionais",
      "Réplicas de VMs e estratégias de failover para continuidade operacional",
      "Dimensionamento de hosts, storage e rede virtual",
    ],
  },
  {
    id: "azure-cloud",
    icon: CloudCog,
    title: "Nuvem Azure e migração híbrida",
    desc: "Arquitetura e transição segura para a nuvem Microsoft Azure, com integração entre ambientes on-premises e cloud em um modelo híbrido.",
    items: [
      "Desenho de arquitetura em Azure alinhada ao cenário e ao orçamento da empresa",
      "Migração de servidores e cargas de trabalho com plano de rollback",
      "Conectividade híbrida entre data center local e nuvem",
      "Governança de recursos, custos e políticas na nuvem",
    ],
  },
  {
    id: "m365-entra",
    icon: IdCard,
    title: "Microsoft 365 e Entra ID",
    desc: "Gestão de identidades em nuvem, licenciamento corporativo, Exchange Online e políticas de acesso seguro para o trabalho moderno.",
    items: [
      "Implantação e administração de Microsoft 365 e Exchange Online",
      "Entra ID com acesso condicional, SSO e políticas de MFA",
      "Governança de identidades, grupos e licenciamento",
      "Migração de e-mails e coexistência com ambientes locais",
    ],
  },
  {
    id: "networks-security",
    icon: Network,
    title: "Redes, VLANs, firewalls e VPNs",
    desc: "Segmentação de rede, VPNs corporativas criptografadas, regras de firewall e prevenção contra acessos indevidos.",
    items: [
      "Segmentação de rede com VLANs e roteamento entre segmentos",
      "Implantação e gestão de firewalls de perímetro com regras mínimas necessárias",
      "VPNs site-to-site e acesso remoto seguro com autenticação reforçada",
      "Qualidade de serviço (QoS) para voz, vídeo e sistemas críticos",
    ],
  },
  {
    id: "web-database",
    icon: Globe2,
    title: "Serviços web Linux e infraestrutura de banco de dados",
    desc: "Infraestrutura de alta performance para webservers e bancos de dados corporativos, com monitoramento e rotinas de manutenção.",
    items: [
      "Webservers NGINX e Apache com TLS, cache e ajuste de performance",
      "Hospedagem de infraestrutura de bancos de dados SQL e NoSQL",
      "Rotinas de manutenção, índices, ajustes de consulta e capacidade",
      "Monitoramento de serviços, logs e alertas proativos",
    ],
  },
  {
    id: "backup-continuity",
    icon: Database,
    title: "Backup e continuidade de negócios",
    desc: "Estratégia de backup em múltiplas camadas, cópias externas imutáveis, testes de restauração periódicos e plano de contingência operacional.",
    items: [
      "Política de backup 3-2-1 com cópias locais e externas",
      "Testes periódicos de restauração e validação de integridade",
      "Planos de disaster recovery com tempos e objetivos de recuperação definidos",
      "Procedimentos de contingência documentados para a operação",
    ],
  },
  {
    id: "audits-support",
    icon: ShieldCheck,
    title: "Auditoria técnica e suporte especializado",
    desc: "Análise de vulnerabilidades, inventário de infraestrutura e suporte técnico N2/N3, preventivo e reativo, comSLA acordado com o cliente.",
    items: [
      "Auditoria de infraestrutura: inventário, configurações e riscos",
      "Recomendações priorizadas por criticidade e impacto no negócio",
      "Suporte especializado N2/N3 para incidentes e demandas técnicas",
      "Rotinas preventivas e acompanhamento contínuo do ambiente",
    ],
  },
];

const ANCHORS = [
  { id: "windows-linux", label: "Windows Server & Linux" },
  { id: "directory-dns", label: "AD, DNS, DHCP & GPO" },
  { id: "hyperv-virtualization", label: "Hyper-V" },
  { id: "azure-cloud", label: "Azure & Híbrido" },
  { id: "m365-entra", label: "Microsoft 365 & Entra ID" },
  { id: "networks-security", label: "Redes & Segurança" },
  { id: "web-database", label: "Web & Banco de Dados" },
  { id: "backup-continuity", label: "Backup & Continuidade" },
  { id: "audits-support", label: "Auditoria & Suporte" },
];

export default function Services() {
  return (
    <div>
      <PageHead
        title="Serviços de TI | Servidores, Redes, Nuvem Azure e Segurança | IPRS"
        description="Soluções completas de infraestrutura de TI: Windows Server e Linux, Active Directory, Hyper-V, Azure, redes, firewalls, backup e suporte especializado."
        path="/servicos"
      />

      <section className="relative pt-28 lg:pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Serviços</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-graphite leading-[1.15] max-w-3xl">
              Soluções completas para ambientes de TI de missão crítica
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base lg:text-lg text-steel leading-relaxed max-w-2xl">
              Da sala de servidores à nuvem, cobrimos todo o ciclo de vida da sua
              infraestrutura: projeto, implementação, administração e suporte.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-x">
        <Reveal>
          <nav aria-label="Índice de serviços" className="flex flex-wrap gap-2 py-6 border-y border-line">
            {ANCHORS.map((a) => (
              <a
                key={a.id}
                href={`#${a.id}`}
                data-testid={`services-anchor-${a.id}`}
                className="text-xs sm:text-sm font-medium text-graphite hover:text-techblue bg-mist hover:bg-pale border border-line rounded-full px-4 py-2 transition-colors duration-200"
              >
                {a.label}
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      <section className="py-14 sm:py-20">
        <div className="container-x space-y-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={0.02}>
              <article
                id={s.id}
                data-testid={`service-section-${s.id}`}
                className="scroll-mt-28 bg-white border border-line rounded-xl overflow-hidden hover:border-techblue/50 transition-colors duration-200"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-4 bg-graphite p-8 lg:p-10">
                    <span className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center text-techblue">
                      <s.icon className="w-5 h-5" />
                    </span>
                    <h2 className="mt-5 text-xl lg:text-2xl font-semibold text-white leading-snug">{s.title}</h2>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.desc}</p>
                  </div>
                  <div className="lg:col-span-8 p-8 lg:p-10">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-techblue">O que entregamos</p>
                    <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
                      {s.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-steel leading-relaxed">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-techblue shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
